import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import Modal from "../../../../Utilities/DraggableModal";
import useFetch from "../../../../Utilities/useFetch";

const ShowTranscriptsModal = ({ show, onHide, debtorID }) => {
  const { REACT_APP_API_URL } = process.env;
  const {
    data: transcripts,
    loading,
    error,
  } = useFetch(
    show && Boolean(debtorID),
    `${REACT_APP_API_URL}/Claims/GetAudioTranscripts`,
    "GET",
    null,
    { debtorID }
  );
  const [expandedItems, setExpandedItems] = useState({});
  const [editedEmailSample, setEditedEmailSample] = useState("");

  const transcriptItems = useMemo(() => {
    if (Array.isArray(transcripts)) return transcripts;
    if (!transcripts || typeof transcripts !== "object") return [];

    if (Array.isArray(transcripts.transcripts)) {
      return transcripts.transcripts;
    }

    return [transcripts];
  }, [transcripts]);

  const getFirstValue = (sources, keys) => {
    for (const source of sources) {
      if (!source || typeof source !== "object") continue;
      for (const key of keys) {
        const value = source[key];
        if (value !== null && value !== undefined && `${value}`.trim() !== "") {
          return value;
        }
      }
    }
    return "";
  };

  const summaryText = useMemo(() => {
    if (transcripts && typeof transcripts === "object") {
      const payloadSummary = transcripts.summary ?? transcripts.Summary;
      if (payloadSummary !== undefined && payloadSummary !== null) {
        return typeof payloadSummary === "string"
          ? payloadSummary
          : JSON.stringify(payloadSummary, null, 2);
      }
    }

    const summary = getFirstValue([transcripts, ...transcriptItems], [
      "summary",
      "Summary",
      "transcriptSummary",
      "callSummary",
    ]);
    return typeof summary === "string" ? summary : JSON.stringify(summary, null, 2);
  }, [transcripts, transcriptItems]);

  const emailSampleText = useMemo(() => {
    if (transcripts && typeof transcripts === "object") {
      const payloadEmail = transcripts.emailSample ?? transcripts.EmailSample;
      if (payloadEmail !== undefined && payloadEmail !== null) {
        return typeof payloadEmail === "string"
          ? payloadEmail
          : JSON.stringify(payloadEmail, null, 2);
      }
    }

    const emailSample = getFirstValue([transcripts, ...transcriptItems], [
      "emailSample",
      "EmailSample",
      "email",
      "Email",
    ]);
    return typeof emailSample === "string"
      ? emailSample
      : JSON.stringify(emailSample, null, 2);
  }, [transcripts, transcriptItems]);

  const hasSummaryContent = Boolean(`${summaryText || ""}`.trim());
  const hasEmailSampleContent = Boolean(`${emailSampleText || ""}`.trim());
  const hasTranscriptContent = transcriptItems.length > 0;

  useEffect(() => {
    setEditedEmailSample(emailSampleText || "");
  }, [summaryText, emailSampleText, show]);

  const formatSeconds = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return "";
    }
    const totalSeconds = Math.floor(Number(value));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = `${totalSeconds % 60}`.padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const parseSegments = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        return [];
      }
    }
    return [];
  };

  const buildTranscriptText = (item) => {
    if (!item || typeof item !== "object") return "";
    const segments = parseSegments(item.data);
    if (segments.length > 0) {
      return segments
        .map((segment) => {
          const speaker = segment?.Speaker ? `${segment.Speaker}: ` : "";
          return `${speaker}${segment?.Text || ""}`.trim();
        })
        .filter(Boolean)
        .join("\n");
    }
    return item.transcription || "";
  };

  const getPreviewText = (text, maxLength = 220) => {
    if (!text) return "";
    const compact = `${text}`.replace(/\s+/g, " ").trim();
    if (compact.length <= maxLength) return compact;
    return `${compact.slice(0, maxLength)}...`;
  };

  const toggleTranscript = (key) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderTranscriptItem = (item, index) => {
    if (!item || typeof item !== "object") {
      return (
        <pre key={index} className="mb-3">
          {JSON.stringify(item, null, 2)}
        </pre>
      );
    }

    const segments = parseSegments(item.data);
    const transcriptText = buildTranscriptText(item);
    const itemKey = item.audioTranscriptID || index;
    const isExpanded = Boolean(expandedItems[itemKey]);
    const previewText = getPreviewText(transcriptText);

    return (
      <div key={itemKey} className="mb-4 border rounded p-3">
        <h6 className="mb-2">Transcript {index + 1}</h6>
        <ul className="mb-3">
          <li>Audio Transcript ID: {item.audioTranscriptID}</li>
          <li>Debtor ID: {item.debtorID}</li>
          <li>Collector: {item.collector || ""}</li>
          <li>From: {item.fromPhoneNumber || ""}</li>
          <li>To: {item.toPhoneNumber || ""}</li>
          <li>Created (UTC): {item.creationDateUTC || ""}</li>
        </ul>
        {previewText ? (
          <div className="mb-2" style={{ whiteSpace: "pre-wrap" }}>
            {isExpanded ? transcriptText : previewText}
          </div>
        ) : null}
        {transcriptText ? (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => toggleTranscript(itemKey)}
            className="mb-3"
          >
            {isExpanded ? "Hide Full Transcript" : "Show Full Transcript"}
          </Button>
        ) : null}
        {isExpanded && segments.length > 0 ? (
          <ul className="mb-0">
            {segments.map((segment) => (
              <li key={segment.Id || `${itemKey}-${segment.StartSecond}`} className="mb-2">
                <div
                  className={segment.Speaker === "Customer" ? "text-primary" : ""}
                >
                  {segment.Speaker ? `${segment.Speaker}: ` : ""}
                  {segment.Text}
                </div>
                <div className="text-muted">
                  {segment.StartSecond !== undefined
                    ? `Start ${formatSeconds(segment.StartSecond)}`
                    : null}
                  {segment.LengthSeconds !== undefined
                    ? ` | Length ${formatSeconds(segment.LengthSeconds)}`
                    : null}
                  {segment.Sentiment?.Suggested !== undefined
                    ? ` | Sentiment ${segment.Sentiment.Suggested}`
                    : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  };
  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable={true}>
      <Modal.Header closeButton>
        <Modal.Title>Transcripts and AI Summary</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!debtorID ? (
          <Alert variant="warning">Debtor ID is required.</Alert>
        ) : loading ? (
          <div className="text-center">
            <Spinner animation="border" />
            <p>Loading transcripts...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">Failed to load transcripts.</Alert>
        ) : (
          <>
            {hasSummaryContent ? (
              <div className="mb-4 border rounded p-3 bg-light">
                <h6 className="mb-2">Summary</h6>
                <Form.Control
                  as="textarea"
                  rows={7}
                  value={summaryText}
                  readOnly
                  placeholder="No summary provided."
                  style={{ whiteSpace: "pre-wrap" }}
                  maxLength={5000}
                />
              </div>
            ) : null}

            {hasEmailSampleContent ? (
              <div className="mb-4 border rounded p-3 bg-light">
                <h6 className="mb-2">Email Sample</h6>
                <Form.Control
                  as="textarea"
                  rows={7}
                  value={editedEmailSample}
                  onChange={(e) => setEditedEmailSample(e.target.value)}
                  placeholder="No email sample provided."
                  style={{ whiteSpace: "pre-wrap" }}
                  maxLength={5000}
                />
              </div>
            ) : null}

            {hasTranscriptContent ? (
              transcriptItems.map(renderTranscriptItem)
            ) : hasSummaryContent || hasEmailSampleContent ? (
              <Alert variant="info" className="mb-0">
                No transcript rows were returned, but summary data is available.
              </Alert>
            ) : (
              <p>No transcripts available.</p>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ShowTranscriptsModal;
